import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useSearchParams } from "react-router-dom";

import { resolveDropInServiceTab } from "./drop-in-service-tab";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { SetupTab } from "./setup-tab";
import { DashboardTab } from "./dashboard-tab";

// DropInServiceHome — the /services/drop-in shell (#143 slice 3). It is the SINGLE
// owner of the page title (ui-quality one-title rule) and renders the content
// selected by the Drop-in live / Drop-in rules navigation pages:
//   - Setup     → check-in mode + staff check-in, drop-in queue config, drop-in
//                 pricing (facility:manage / facility:price gated inside the tab)
//   - Live       → the live front-desk console (checked-in roster + queue), a pure
//                 composition of existing workspaces rendered `embedded` so this
//                 shell keeps the only <h1>.
//
// The active tab is driven ENTIRELY by the `?tab=` query (default "setup", so a
// bare /services/drop-in opens Setup). The modes shell owns the links; this component
// owns only content selection, so there is no duplicate outer tab row or local
// navigation state to drift. The facility is resolved from FacilityContext; the
// Layer-3 gate owns the genuine "no facility" case, so this shell only shows its
// own brief loading affordance during the resolve window.

export function DropInServiceHome() {
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();
  const [searchParams] = useSearchParams();
  const activeTab = resolveDropInServiceTab(searchParams);

  if (facilityLoading || !facilityId || !facility) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          minHeight: "40vh",
          display: "grid",
          placeItems: "center",
          px: { xs: 2, lg: 3 },
          color: "text.secondary",
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading…</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      <Box>
        <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
          {activeTab === "setup" ? "Drop-in setup" : <>Live drop-in</>}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {facility.name}
        </Typography>
      </Box>

      {activeTab === "setup" ? (
        <SetupTab key={facilityId} facilityId={facilityId} />
      ) : (
        <DashboardTab facilityId={facilityId} timeZone={facility.timezone} />
      )}
    </Stack>
  );
}
